import React, { useState, useEffect, useMemo } from 'react';
import { useApi } from '../../api/api';
import './Stock.css';
import { useParams } from 'react-router-dom';
import { StockDataProvider } from '../../contexts/providers/StockDataProvider';

/** Plan slug → model type. Model is fully determined by plan; no separate model selector. */
const PLAN_SLUG_TO_MODEL = {
  'ne-multi-groundfish': 'WHAM',
  'sea-scallop': 'CASA/SAMS',
  'american-lobster': 'LOBSTER'
};

export default function Stock() {
  const { regionSlug, planSlug, stockSlug } = useParams();
  const { getStocksBySlugs, getPageChildren } = useApi();
  const [stockIdForLayout, setStockIdForLayout] = useState(null);
  const [stockUuidForLayout, setStockUuidForLayout] = useState(null);
  const [stockNameForLayout, setStockNameForLayout] = useState(null);
  const [stockImageForLayout, setStockImageForLayout] = useState(null);
  const [topPanelModules, setTopPanelModules] = useState([]);

  useEffect(() => {
    const fetchStockForLayout = async () => {
      if (!regionSlug || !planSlug || !stockSlug) return;
      try {
        const stocksResponse = await getStocksBySlugs(stockSlug, planSlug, regionSlug);
        const stock = stocksResponse?.data;
        const stockIdNum = stock?.stock_id ?? stock?.fishery_management_stock_id ?? stock?.id ?? null;
        const stockUuid = stock?.stock_uuid ?? null;
        const stockName = stock?.stock_name ?? null;
        const stockImage = stock?.stock_image ?? stock?.image_url ?? null;
        setStockIdForLayout(stockIdNum);
        setStockUuidForLayout(stockUuid);
        setStockNameForLayout(stockName);
        setStockImageForLayout(stockImage);
      } catch (err) {
        console.error('Error fetching stock:', err);
        setStockIdForLayout(null);
        setStockUuidForLayout(null);
        setStockNameForLayout(null);
        setStockImageForLayout(null);
      }
    };
    fetchStockForLayout();
  }, [regionSlug, planSlug, stockSlug, getStocksBySlugs]);

  useEffect(() => {
    const fetchPageChildren = async () => {
      if (!stockIdForLayout) {
        setTopPanelModules([]);
        return;
      }
      try {
        const response = await getPageChildren('stock', stockIdForLayout);
        const children = response?.data ?? [];
        const modules = Array.isArray(children)
          ? children
              .map((child) => ({
                id: String(child.ui_page_id ?? child.id ?? child.name),
                name: child.name ?? 'Unknown',
                resource_id: child.resource_id ?? child.ui_page_id ?? child.id,
                page_order: typeof child.page_order === 'number' ? child.page_order : 999
              }))
              .sort((a, b) => a.page_order - b.page_order)
          : [];
        setTopPanelModules(modules);
      } catch (err) {
        console.error('Error fetching page children:', err);
        setTopPanelModules([]);
      }
    };
    fetchPageChildren();
  }, [stockIdForLayout, getPageChildren]);

  const selectedModel = useMemo(
    () => PLAN_SLUG_TO_MODEL[planSlug] ?? 'WHAM',
    [planSlug]
  );

  return (
    <StockDataProvider
      regionSlug={regionSlug}
      planSlug={planSlug}
      stockSlug={stockSlug}
      stockIdForLayout={stockIdForLayout}
      stockUuidForLayout={stockUuidForLayout}
      stockNameForLayout={stockNameForLayout}
      stockImageForLayout={stockImageForLayout}
      topPanelModules={topPanelModules.length > 0 ? topPanelModules : null}
      selectedModel={selectedModel}
    />
  );
}
